import { LaunchGameData } from "@/interfaces/api/games/api_request_types";
import { AddProfileData, EditProfileData } from "@/interfaces/api/profiles/api_request_types";
import { API_Data_Add_Profile_Response, API_Data_Delete_Profile_Response, API_Data_Edit_Profile_Response, API_Data_History_Response, API_Data_Profiles_Response } from "@/interfaces/api/profiles/api_response_types";
import { API_Error, API_Response } from "@/interfaces/api/response/api_response_types";
import { fetcher } from "@/utils/helpers";

/*
==================
      PROFILES
==================
*/

/**
 * Retrieves the authenticated user's profiles from the backend
 * 
 * @returns An array of user profiles.
 * @throws {API_Error} If the request fails or the response is invalid.
 */
export async function listProfiles (): Promise<API_Data_Profiles_Response> {
  const res = await fetcher(`/api/profiles`, { cache: "no-store" })
  const response = await res.json() as API_Response<API_Data_Profiles_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknow profiles list error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Profiles data missing in response' } as API_Error

  return response.data
}

/**
 * Create a new profile on server
 * 
 * @param payload - The data required to create a new profile
 * @returns - A promise that resolves with the new profile data
 */
export async function addProfile (payload: AddProfileData): Promise<API_Data_Add_Profile_Response> {
  const res = await fetcher("/api/self/profiles", { 
    method: 'POST',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  })
  const response = await res.json() as API_Response<API_Data_Add_Profile_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknow add profile error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Add profile data missing in response' } as API_Error

  return response.data
}

/**
 * Edits a profile’s data on the server
 * 
 * @param param0 - An object that contains the profileId and a payload with the data to be sent to the server
 * @returns - Returns the data of the edited profile
 */
export async function editProfile ({ profileId, payload }: { profileId: string, payload: EditProfileData }): Promise<API_Data_Edit_Profile_Response> {
  const res = await fetcher(`/api/self/profiles/${profileId}`, { 
    method: 'PUT',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  })
  const response = await res.json() as API_Response<API_Data_Edit_Profile_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknow add profile error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Add profile data missing in response' } as API_Error

  return response.data
}

/**
 * Delete a profile on the server
 * 
 * @param profileId Profile identifier
 * @returns 
 */
export async function deleteProfile (profileId: string): Promise<API_Data_Delete_Profile_Response> {
  const res = await fetcher(`/api/self/profiles/${profileId}`, { 
    method: 'DELETE',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    }
  })
  const response = await res.json() as API_Response<API_Data_Delete_Profile_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknow add profile error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Delete profile data missing in response' } as API_Error

  return response.data
}

/**
 * Get the game history of a profile 
 * 
 * @param profileId Profile identifier
 * @returns 
 */
export async function listHistory (profileId: string): Promise<API_Data_History_Response> {
  const res = await fetcher(`/api/self/profiles/${profileId}/history`, { 
    method: 'GET',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    }
  })
  const response = await res.json() as API_Response<API_Data_History_Response>

  if (!res.ok) throw { code: res.status, description: 'Unknow game history error' } as API_Error
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Game history data missing in response' } as API_Error

  return response.data
}


export async function launchGame({ profileId, payload }: { profileId: string, payload: LaunchGameData }) {
  const res = await fetcher(`/api/profiles/${profileId}/execs` , {
    method: 'POST',
    cache: "no-store",
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  })
  
  const response = await res.json() as API_Response<any>
  
  if (!res.ok) throw response
  if (response.status === 'error') throw response.error
  if (!response.data) throw { code: 500, description: 'Game launch data missing in response' } as API_Error

  return response.data
}